
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface HeroSimulatorProps {
  selectedVehicle: string;
}

export default function HeroSimulator({ selectedVehicle }: HeroSimulatorProps) {
  const [key, setKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      // 車両が変更されたらiframeを強制的に再マウント
      setKey(prev => prev + 1);
      setError(null);
      setIsLoading(true);
    } catch (err) {
      setError('Failed to load simulator');
      setIsLoading(false);
    }
  }, [selectedVehicle]);

  const simulatorUrl = `https://3dcars.jp/embed/data/${selectedVehicle}`;

  const handleReload = () => {
    try {
      setIsLoading(true);
      setError(null);
      setKey(prev => prev + 1);
    } catch (err) {
      setError('Failed to reload simulator');
      setIsLoading(false);
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setError('Failed to load simulator');
    setIsLoading(false);
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#00d4ff 1px, transparent 1px), linear-gradient(90deg, #00d4ff 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Particles Animation */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#00d4ff] rounded-full"
            animate={{
              x: [Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000), Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000)],
              y: [Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000), Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: 'linear'
            }}
            style={{
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%'
            }}
          />
        ))}
      </div>

      {/* 3D Simulator - Full Screen */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="absolute inset-0"
      >
        <div className="w-full h-full overflow-hidden shadow-[0_0_30px_rgba(0,212,255,0.3)]">
          {/* Loading Indicator */}
          {isLoading && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
              <div className="text-center">
                <div className="inline-block w-12 h-12 border-4 border-[#00d4ff] border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-white text-lg">シミュレーター読み込み中...</p>
              </div>
            </div>
          )}

          {error ? (
            <div className="flex items-center justify-center h-full bg-black bg-opacity-50">
              <div className="text-center">
                <p className="text-red-500 mb-4">{error}</p>
                <button
                  onClick={handleReload}
                  className="px-6 py-3 bg-[#00d4ff] text-black font-bold rounded-full hover:bg-[#00b8e6] transition-all cursor-pointer whitespace-nowrap"
                >
                  再読み込み
                </button>
              </div>
            </div>
          ) : (
            <iframe
              key={key}
              src={simulatorUrl}
              className="w-full h-full border-0"
              title="3D Wheel Simulator"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <i className="ri-arrow-down-line text-[#39ff14] text-3xl"></i>
      </motion.div>
    </section>
  );
}
